// src/pages/SubContractsPage.tsx
import React, { useMemo, useState, useCallback } from "react";
import { useData } from "../DataContext";
import { generateId } from "../utils/id";
import { SubContract } from "../data/mockData";
import { projectDisplayLabelPretty, subContractDisplayLabelPretty } from "../utils/format";
import { useTranslation } from "react-i18next";

function IconCheck({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconUndo({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9 14 4 9l5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 9h10a6 6 0 0 1 0 12h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPencil({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconTrash({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M7 6l1 16h8l1-16" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function SubContractsPage() {
  const { t } = useTranslation();
  const {
    clients,
    projects,
    subContracts,
    resources,
    expenses,
    addSubContract,
    updateSubContract,
    deleteSubContract,
  } = useData() as any;

  // -------------------------
  // Page controls
  // -------------------------
  const [search, setSearch] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // -------------------------
  // Add form
  // -------------------------
  const [projectId, setProjectId] = useState("");
  const [subContractorId, setSubContractorId] = useState("");
  const [title, setTitle] = useState("");
  const [scope, setScope] = useState("");
  const [amount, setAmount] = useState("");

  // -------------------------
  // Edit state
  // -------------------------
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editScope, setEditScope] = useState("");
  const [editAmount, setEditAmount] = useState("");

  const selectedProject = useMemo(
    () => projects.find((p: any) => p.id === projectId),
    [projects, projectId]
  );

  const projectLabel = (pid: string) => {
    const p = projects.find((x: any) => x.id === pid);
    return p ? projectDisplayLabelPretty(p, clients) : pid;
  };

  const dbSubcontractors = useMemo(() => {
    return (resources || [])
      .filter((r: any) => Number(r.isSubcontractor ?? r.is_subcontractor ?? 0) === 1)
      .map((r: any) => ({
        id: r.id,
        name: r.name,
      }));
  }, [resources]);

  const contractorName = (cid: string) =>
    dbSubcontractors.find((c: any) => c.id === cid)?.name || cid;

  // FIX (Cloudflare/CI): stabilize this function so useMemo deps are valid
  const paidToDateFor = useCallback(
    (subId: string) =>
      expenses
        .filter((e: any) => e.expenseType === "Sub-Contract Payment" && e.subContractId === subId)
        .reduce((s: number, e: any) => s + (Number(e.amount) || 0), 0),
    [expenses]
  );

  const hasPayments = (subId: string) =>
    expenses.some((e: any) => e.expenseType === "Sub-Contract Payment" && e.subContractId === subId);

  // -------------------------
  // Visible rows (search + computed totals)
  // -------------------------
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return [...(subContracts || [])]
      .map((sc: any) => {
        const paid = Number(sc.paidToDate ?? paidToDateFor(sc.id) ?? 0);
        const contract = Number(sc.contractAmount ?? 0);
        const balance = Number(sc.balance ?? Math.max(contract - paid, 0));

        const p = projects.find((x: any) => x.id === sc.projectId);
        const c = dbSubcontractors.find((x: any) => x.id === sc.subContractorId);

        const projectPretty = p ? projectDisplayLabelPretty(p, clients) : sc.projectId;
        const contractorPretty = c?.name || sc.subContractorId;

        const subPretty = subContractDisplayLabelPretty(sc, projects, clients, dbSubcontractors);

        return {
          ...sc,
          _projectPretty: projectPretty,
          _contractorPretty: contractorPretty,
          _subPretty: subPretty,
          _paid: paid,
          _balance: balance,
        };
      })
      .filter((r: any) => {
        if (!q) return true;

        const hay = [
          r.id,
          r._subPretty,
          r._projectPretty,
          r.projectId,
          r.projectAddress || "",
          r._contractorPretty,
          r.subContractorId,
          r.title || "",
          r.scope || "",
          String(r.contractAmount ?? ""),
        ]
          .join(" ")
          .toLowerCase();

        return hay.includes(q);
      })
      .sort((a: any, b: any) => {
        const ba = Number(a._balance || 0);
        const bb = Number(b._balance || 0);
        if (ba !== bb) return bb - ba;
        return (Number(b.contractAmount) || 0) - (Number(a.contractAmount) || 0);
      });
  }, [subContracts, projects, dbSubcontractors, search, clients, paidToDateFor]);

  const totals = useMemo(() => {
    const contract = rows.reduce((s: number, r: any) => s + (Number(r.contractAmount) || 0), 0);
    const paid = rows.reduce((s: number, r: any) => s + (Number(r._paid) || 0), 0);
    const bal = rows.reduce((s: number, r: any) => s + (Number(r._balance) || 0), 0);
    return { contract, paid, bal };
  }, [rows]);

  // -------------------------
  // Add / Edit handlers
  // -------------------------
  const resetAdd = () => {
    setProjectId("");
    setSubContractorId("");
    setTitle("");
    setScope("");
    setAmount("");
    setFormError(null);
  };

  const handleAdd = () => {
    if (!projectId || !subContractorId || !title.trim() || !scope.trim() || !amount) {
      setFormError("Project, Subcontractor, Title, Scope, and Amount are required.");
      return;
    }

    const amt = Number(amount);
    if (Number.isNaN(amt) || amt <= 0) {
      setFormError("Amount must be a valid number greater than 0.");
      return;
    }

    addSubContract({
      id: generateId("SUB"),
      projectId,
      projectAddress: selectedProject?.address || "",
      subContractorId,
      title: title.trim(),
      scope: scope.trim(),
      contractAmount: amt,
    });

    resetAdd();
  };

  const startEdit = (sc: SubContract) => {
    setEditingId((sc as any).id);
    setEditTitle((sc as any).title || "");
    setEditScope((sc as any).scope || "");
    setEditAmount(String((sc as any).contractAmount ?? ""));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditScope("");
    setEditAmount("");
  };

  const saveEdit = (sc: SubContract) => {
    // In ledger, we’re editing only Title/Scope/Amount (project + subcontractor stay fixed here).
    if (!editTitle.trim() || !editScope.trim() || !editAmount) {
      alert(t("subContracts.requiredFields"));
      return;
    }

    const amt = Number(editAmount);
    if (Number.isNaN(amt) || amt <= 0) {
      alert(t("subContracts.amountInvalid"));
      return;
    }

    updateSubContract({
      ...(sc as any),
      title: editTitle.trim(),
      scope: editScope.trim(),
      contractAmount: amt,
    });

    cancelEdit();
  };

  const doDelete = (sc: SubContract) => {
    if (hasPayments((sc as any).id)) {
      alert(
        "Cannot delete this sub-contract because payments exist for it.\n\nDelete/edit the related expense entries first."
      );
      return;
    }

    const ok = window.confirm(t("subContracts.deleteConfirm", { title: (sc as any).title }));
    if (!ok) return;

    deleteSubContract((sc as any).id);
    if (editingId === (sc as any).id) cancelEdit();
  };

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className="container">
      {/* Page header (title left, search top-right) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ marginBottom: 6 }}>Sub-Contracts</h1>
          <div className="pageNote">{t("subContracts.pageNote")}</div>
        </div>

        <div style={{ minWidth: 320, maxWidth: 520, flex: "1 1 360px" }}>
          <label style={{ display: "block", fontSize: 12, color: "var(--pageMuted)" }}>{t("common.search")}</label>
          <input
            className="input"
            placeholder={t("subContracts.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {/* ADD SUB-CONTRACT */}
      <div className="card cardTight" style={{ marginTop: 14, marginBottom: 14 }}>
        <div className="cardHeader">
          <h3 className="cardTitle">{t("subContracts.addTitle")}</h3>
          <div className="cardSub">
            <span className="reqStar">*</span> {t("common.required")}
          </div>
        </div>

        <div className="formGrid">
          {/* Row 1: Project full width */}
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label className="label">
              {t("common.project")} <span className="reqStar">*</span>
            </label>
            <select
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
                if (formError) setFormError(null);
              }}
              style={{ width: "100%" }}
            >
              <option value="">{t("subContracts.selectProject")}</option>
              {projects.map((p: any) => (
                <option key={p.id} value={p.id}>
                  {projectLabel(p.id)}
                </option>
              ))}
            </select>
          </div>

          {/* Row 2: Subcontractor + Amount */}
          <div className="field">
            <label className="label">
              {t("subContracts.subcontractorLabel")} <span className="reqStar">*</span>
            </label>
            <select
              value={subContractorId}
              onChange={(e) => {
                setSubContractorId(e.target.value);
                if (formError) setFormError(null);
              }}
              style={{ width: "100%" }}
            >
              <option value="">{t("subContracts.selectSubcontractor")}</option>
              {dbSubcontractors.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="label">
              {t("subContracts.contractAmountLabel")} <span className="reqStar">*</span>
            </label>
            <input
              className="input"
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                if (formError) setFormError(null);
              }}
              style={{ width: "100%" }}
            />
          </div>

          {/* Row 3: Title full width */}
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label className="label">
              {t("subContracts.titleLabel")} <span className="reqStar">*</span>
            </label>
            <input
              className="input"
              placeholder={t("subContracts.titlePlaceholder")}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (formError) setFormError(null);
              }}
              style={{ width: "100%" }}
            />
          </div>

          {/* Row 4: Scope full width */}
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label className="label">
              {t("subContracts.scopeLabel")} <span className="reqStar">*</span>
            </label>
            <textarea
              className="input"
              placeholder={t("subContracts.scopePlaceholder")}
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                if (formError) setFormError(null);
              }}
              style={{ width: "100%", minHeight: 86, resize: "vertical" }}
            />
          </div>
        </div>

        {formError && (
          <div className="formError" role="alert">
            {formError}
          </div>
        )}

        <div className="formActions">
          <button className="btn" onClick={handleAdd} type="button">{t("subContracts.addBtn")}</button>
          <button className="btn btnSecondary" onClick={resetAdd} type="button">{t("common.clear")}</button>
        </div>
      </div>

      {/* TOTALS (filtered) */}
      <div className="card" style={{ padding: 12, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 1000 }}>
            Totals (filtered){" "}
            <span className="badge" style={{ marginLeft: 8 }} title={t("subContracts.filterTip")}>
              {rows.length} rows
            </span>
          </div>

          <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", fontSize: 13 }}>
            <span style={{ color: "var(--muted)" }}>
              Contract: <b style={{ color: "var(--text)" }}>{money(totals.contract)}</b>
            </span>
            <span style={{ color: "var(--muted)" }}>
              {t("subContracts.paidLabel")} <b className="numWarn">{money(totals.paid)}</b>
            </span>
            <span style={{ color: "var(--muted)" }}>
              {t("subContracts.balanceLabel")} <b className={totals.bal > 0 ? "numBad" : "numGood"}>{money(totals.bal)}</b>
            </span>
          </div>
        </div>
      </div>

      {/* LEDGER */}
      <div className="card">
        <div className="cardHeader">
          <div>
            <h3 className="cardTitle">{t("subContracts.ledgerTitle")}</h3>
            <div className="cardSub">{rows.length} rows</div>
          </div>

          <span className="badge" title={t("subContracts.paymentsTip")}>
            Ledger
          </span>
        </div>

        <div className="tableWrap tableWrapY">
          <table style={{ fontSize: 13 }}>
            <thead>
              {/* FIX: single TR only (no nested tr) */}
              <tr>
                <th style={{ width: 110 }}>ID</th>
                <th>{t("subContracts.colSubContract")}</th>
                <th>{t("subContracts.colScope")}</th>
                <th style={{ width: 130, textAlign: "right" }}>{t("common.contract")}</th>
                <th style={{ width: 130, textAlign: "right" }}>{t("common.paid")}</th>
                <th style={{ width: 130, textAlign: "right" }}>{t("common.balance")}</th>
                <th style={{ width: 140 }}>{t("common.actions")}</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((sc: any) => {
                const isEditing = editingId === sc.id;
                const paid = Number(sc._paid || 0);
                const bal = Number(sc._balance || 0);

                return (
                  <tr key={sc.id} style={bal > 0 ? { background: "rgba(229,83,61,0.04)" } : undefined}>
                    <td style={{ fontWeight: 900 }}>{sc.id}</td>

                    <td>
                      {isEditing ? (
                        <div style={{ display: "grid", gap: 10 }}>
                          <div>
                            <label className="label" style={{ marginBottom: 6 }}>
                              Title
                            </label>
                            <input className="input" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} style={{ width: "100%" }} />
                          </div>

                          <div style={{ fontSize: 12, color: "var(--muted)" }}>
                            <div>
                              <b>{contractorName(sc.subContractorId)}</b>
                            </div>
                            <div>{sc._projectPretty}</div>
                          </div>
                        </div>
                      ) : (
                        <span className="cellClamp" title={sc._subPretty}>
                          {sc._subPretty}
                        </span>
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <textarea
                          className="input"
                          value={editScope}
                          onChange={(e) => setEditScope(e.target.value)}
                          style={{ width: "100%", minHeight: 64, resize: "vertical" }}
                        />
                      ) : (
                        <span className="cellClamp" title={sc.scope || ""}>
                          {sc.scope || "-"}
                        </span>
                      )}
                    </td>

                    <td style={{ textAlign: "right", fontWeight: 900 }}>
                      {isEditing ? (
                        <input
                          className="input"
                          type="number"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          style={{ width: "100%", textAlign: "right" }}
                        />
                      ) : (
                        money(sc.contractAmount)
                      )}
                    </td>

                    <td style={{ textAlign: "right" }} className={paid > 0 ? "numWarn" : ""}>
                      {money(paid)}
                    </td>

                    <td style={{ textAlign: "right" }} className={bal > 0 ? "numBad" : "numGood"}>
                      {money(bal)}
                    </td>

                    <td>
                      {isEditing ? (
                        <div className="actionIcons">
                          <button className="iconActionBtn primary" onClick={() => saveEdit(sc)} type="button" title={t("subContracts.saveBtn")}>
                            <IconCheck />
                          </button>
                          <button className="iconActionBtn" onClick={cancelEdit} type="button" title={t("subContracts.cancelBtn")}>
                            <IconUndo />
                          </button>
                        </div>
                      ) : (
                        <div className="actionIcons">
                          <button className="iconActionBtn primary" onClick={() => startEdit(sc)} type="button" title={t("subContracts.editBtn")}>
                            <IconPencil />
                          </button>

                          <button
                            className="iconActionBtn danger"
                            onClick={() => doDelete(sc)}
                            type="button"
                            title={hasPayments(sc.id) ? t("subContracts.deleteDisabled") : t("common.delete")}
                            disabled={hasPayments(sc.id)}
                          >
                            <IconTrash />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 14, color: "var(--muted)", fontStyle: "italic" }}>
                    {t("subContracts.noSubContracts")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pageNote" style={{ marginTop: 10 }}>
          {t("subContracts.noteTip")}
        </div>
      </div>
    </div>
  );
}

